import React, { useEffect, useState } from 'react';
import {
  Container,
  Heading,
  SimpleGrid,
  Card,
  CardBody,
  Text,
  HStack,
  VStack,
  List,
  ListItem,
  Image,
  Box,
  Divider,
  useToast,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
} from '@chakra-ui/react';
import { db } from '../../lib/firebase';
import { collection, getDocs, query, where, DocumentData } from 'firebase/firestore';
import { Match, Prediction, User } from '../../types';
import { rankingService } from '../../services/rankingService';

interface FirestoreUser {
  id: string;
  name: string;
  email: string;
  displayName?: string;
}

interface FirestoreMatch extends Match {
  predictions: Array<{
    user: {
      name: string;
      avatar?: string;
    };
    predicted_score_a: number;
    predicted_score_b: number;
  }>;
}

const VALID_USERS = [
  { email: 'htorres@teiker.mx', name: 'HectorT' },
  { email: 'ralvarez@teiker.mx', name: 'Ramiro' },
  { email: 'ohernandez@teiker.mx', name: 'Oscar 🛌' },
  { email: 'jhernandez@teiker.mx', name: 'Chuy 🧑🏿‍🦱' },
  { email: 'erodriguez@teiker.mx', name: 'Neto' },
  { email: 'gmartinez@teiker.mx', name: 'Gus' },
  { email: 'lgama@teiker.mx', name: 'Gama 🧙🏻‍♂️' },
  { email: 'e.lugo@teiker.mx', name: 'Lugo 🎱' },
  { email: 'mrangel@teiker.mx', name: 'Alex' },
  { email: 'frojo@teiker.mx', name: 'Fer 🧂' },
  { email: 'lcardona@teiker.mx', name: 'Cardona' },
  { email: 'egonzalez@teiker.mx', name: 'Edgar' },
  { email: 'juanjo@juanjo.mx', name: 'JuanJo 👶🏼' },
  { email: 'alicia@teiker.mx', name: 'Alicia 🪦' },
  { email: 'cflores@teiker.mx', name: 'Charly' },
  { email: 'jmontemayor@teiker.mx', name: 'Jeannesy' },
  { email: 'palcala@teiker.mx', name: 'Paty' },
  { email: 'vtorres@teiker.mx', name: 'Vianey' },
  { email: 'calvarez@teiker.mx', name: 'Ivon' },
  { email: 'jherrera@teiker.mx', name: 'Pepe 💅🏻' }
];

const getUserAvatar = (name: string): string | undefined => {
  switch (name) {
    case 'Ramiro':
      return '/images/ramiro.png';
    case 'Gus':
      return '/images/el_gus.png';
    case 'Neto':
      return '/images/neto2.png';
    default:
      return undefined;
  }
};

const CapturedResults: React.FC = () => {
  const [matches, setMatches] = useState<FirestoreMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const [teamLogos, setTeamLogos] = useState<{ [teamName: string]: string }>({});

  // Obtener todas las fases únicas
  const allPhases = Array.from(new Set(matches.map(m => m.phase))).filter(Boolean);

  // Estado para la fase seleccionada
  const [selectedPhase, setSelectedPhase] = useState<string | null>(null);

  // Filtrar partidos por fase seleccionada
  const filteredMatches = selectedPhase
    ? matches.filter(m => m.phase === selectedPhase)
    : matches;

  const tabBg = useColorModeValue('blue.50', 'blue.900');
  const tabSelectedBg = useColorModeValue('blue.500', 'blue.300');
  const tabSelectedColor = useColorModeValue('white', 'gray.900');

  const resultadoOficialColor = useColorModeValue('gray.600', 'gray.200');

  useEffect(() => {
    loadMatches();
  }, []);

  useEffect(() => {
    const fetchLogos = async () => {
      const logos: { [teamName: string]: string } = {};
      for (const match of matches) {
        if (match.team_a && !logos[match.team_a]) {
          logos[match.team_a] = (await rankingService.getTeamLogo(match.team_a)) || '';
        }
        if (match.team_b && !logos[match.team_b]) {
          logos[match.team_b] = (await rankingService.getTeamLogo(match.team_b)) || '';
        }
      }
      setTeamLogos(logos);
    };

    if (matches.length > 0) {
      fetchLogos();
    }
  }, [matches]);

  const loadMatches = async () => {
    try {
      // 1. Obtener partidos activos (sin filtro de phase)
      const matchesRef = collection(db, 'matches');
      const matchesSnap = await getDocs(query(matchesRef, where('activo', '==', true)));
      const matchesData = matchesSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          phase: data.phase,
          team_a: data.team_a,
          team_b: data.team_b,
          score_a: data.score_a,
          score_b: data.score_b,
          activo: data.activo,
          team_a_logo: data.team_a_logo,
          team_b_logo: data.team_b_logo,
          date: data.date,
          is_first_leg: data.is_first_leg,
          created_at: data.created_at,
          updated_at: data.updated_at,
          predictions: []
        } as FirestoreMatch;
      });

      // 2. Obtener todas las predicciones SOLO de los partidos activos
      const predictionsRef = collection(db, 'predictions');
      const predictionsSnap = await getDocs(predictionsRef);
      const allPredictions: any[] = [];
      predictionsSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        // Estructura 1: predicción por partido
        if (data.match_id && data.activo === 1 && matchesData.some(m => m.id === data.match_id)) {
          allPredictions.push({ ...data, id: docSnap.id });
        }
        // Estructura 2: array de predicciones por usuario
        if (Array.isArray(data.predictions)) {
          data.predictions.forEach((p: any) => {
            if (p.activo === 1 && matchesData.some(m => m.id === p.match_id)) {
              allPredictions.push({ ...p, user_id: data.user_id });
            }
          });
        }
      });

      // Verificar predicciones del usuario específico
      const userPredictions = allPredictions.filter(p => p.user_id === 'v2aquJjaOeTIU9ADuk93cEwHV8O2');
      

      // 3. Obtener todos los usuarios
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);
      const users = new Map<string, FirestoreUser>();
      usersSnap.docs.forEach(doc => {
        const userData = doc.data();
        if (userData.uuid) {
          users.set(userData.uuid, {
            id: doc.id,
            name: userData.name || '',
            email: userData.email || '',
            displayName: userData.displayName
          });
        }
      });

      // Verificar si existe el usuario
      const specificUser = users.get('v2aquJjaOeTIU9ADuk93cEwHV8O2');
      

      // 4. Mapear predicciones a partidos
      matchesData.forEach(match => {
        const matchPredictions = allPredictions.filter(p => p.match_id === match.id);
        match.predictions = matchPredictions.map(prediction => {
          const user = users.get(prediction.user_id);
          const validUser = VALID_USERS.find(u => u.email === user?.email);
          
          return {
            user: {
              name: validUser?.name || user?.name || prediction.userName || 'Usuario Desconocido',
              avatar: getUserAvatar(validUser?.name || '')
            },
            predicted_score_a: prediction.predicted_score_a,
            predicted_score_b: prediction.predicted_score_b
          };
        });
      });

      setMatches(matchesData);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const groupMatchesByPhase = (matches: FirestoreMatch[]) => {
    const groupedMatches: { [key: string]: FirestoreMatch[] } = {};
    
    matches.forEach(match => {
      if (!groupedMatches[match.phase]) {
        groupedMatches[match.phase] = [];
      }
      groupedMatches[match.phase].push(match);
    });

    // Ordenar los partidos dentro de cada fase por ida y vuelta
    Object.keys(groupedMatches).forEach(phase => {
      groupedMatches[phase].sort((a, b) => {
        // Primero los de ida (is_first_leg = true)
        if (a.is_first_leg && !b.is_first_leg) return -1;
        if (!a.is_first_leg && b.is_first_leg) return 1;
        return 0;
      });
    });

    return groupedMatches;
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={5}>
        <Text>Cargando resultados...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={5}>
      <Heading mb={6}>Predicciones Capturadas</Heading>

      {/* NavTab para filtrar por fase */}
      <Tabs
        variant="enclosed"
        colorScheme="blue"
        mb={6}
        onChange={idx => setSelectedPhase(idx === 0 ? null : allPhases[idx - 1])}
        isFitted
      >
        <TabList bg={tabBg} borderRadius="md" p={2}>
          <Tab
            key="all"
            _selected={{ color: tabSelectedColor, bg: tabSelectedBg }}
            borderRadius="md"
            fontWeight="bold"
          >
            Todas
          </Tab>
          {allPhases.map(phase => (
            <Tab
              key={phase}
              _selected={{ color: tabSelectedColor, bg: tabSelectedBg }}
              borderRadius="md"
              fontWeight="bold"
            >
              {phase}
            </Tab>
          ))}
        </TabList>
      </Tabs>

      {/* Mostrar partidos filtrados */}
      {Object.entries(groupMatchesByPhase(filteredMatches)).map(([phase, phaseMatches]) => (
        <Box key={phase} mb={8}>
          <Heading size="md" mb={4}>{phase}</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {phaseMatches.map((match) => (
              <Card key={match.id}>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    {/* Encabezado del partido */}
                    <HStack justify="space-between" width="100%">
                      <HStack>
                        <Image
                          src={teamLogos?.[match.team_a || ''] || '/team-placeholder.png'}
                          alt={match.team_a || 'Equipo local'}
                          boxSize="40px"
                          objectFit="contain"
                          borderRadius="full"
                        />
                        <Text fontWeight="bold">{match.team_a}</Text>
                      </HStack>
                      <Text color="gray.500">VS</Text>
                      <HStack>
                        <Text fontWeight="bold">{match.team_b}</Text>
                        <Image
                          src={teamLogos?.[match.team_b || ''] || '/team-placeholder.png'}
                          alt={match.team_b || 'Equipo visitante'}
                          boxSize="40px"
                          objectFit="contain"
                          borderRadius="full"
                        />
                      </HStack>
                    </HStack>

                    {/* Indicador de ida/vuelta */}
                    <Text color="blue.500" textAlign="center" fontWeight="bold">
                      {match.phase
                        ? `Partido de ${match.phase}`
                        : 'Partido'}
                    </Text>

                    {/* Resultado oficial */}
                    <Text color={resultadoOficialColor} textAlign="center">
                      Resultado oficial: {match.score_a ?? '-'} - {match.score_b ?? '-'}
                    </Text>

                    <Divider />

                    {/* Lista de predicciones */}
                    <Box>
                      <Text fontWeight="bold" mb={2}>Predicciones:</Text>
                      {match.predictions.length === 0 ? (
                        <Text color="gray.500">No hay predicciones capturadas.</Text>
                      ) : (
                        <Table variant="simple">
                          <Thead>
                            <Tr>
                              <Th>Usuario</Th>
                              <Th>Predicción</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {match.predictions
                              .filter(prediction => prediction.user.name !== 'Usuario Desconocido')
                              .map((prediction, idx) => (
                                <Tr key={idx}>
                                  <Td>
                                    <HStack>
                                      <Text>{prediction.user.name}</Text>
                                      {prediction.user.avatar && (
                                        <Image
                                          src={prediction.user.avatar}
                                          alt={prediction.user.name}
                                          boxSize={prediction.user.name === 'Gus' ? "35px" : "25px"}
                                          objectFit="contain"
                                        />
                                      )}
                                    </HStack>
                                  </Td>
                                  <Td>
                                    {prediction.predicted_score_a} - {prediction.predicted_score_b}
                                  </Td>
                                </Tr>
                              ))}
                          </Tbody>
                        </Table>
                      )}
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>
      ))}
    </Container>
  );
};

export default CapturedResults; 